import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";
import SiteDetailsDialog from "@/components/admin/site-details-dialog";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { AdminGuard } from "@/components/role-guard";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type AdminSiteRow = inferRouterOutputs<AppRouter>["admin"]["listSites"][number];

type SiteSelection = {
  id: string;
  name: string;
};

export default function AdminSites() {
  const sitesQuery = useQuery({
    ...trpc.admin.listSites.queryOptions(),
    placeholderData: (previousData) => previousData,
  });
  const [selectedSite, setSelectedSite] = useState<SiteSelection | null>(null);
  const sites = (sitesQuery.data ?? []) as AdminSiteRow[];

  return (
    <AdminGuard>
      <PageContainer className="flex min-h-full flex-col gap-6">
        <PageHeader
          title="Sites overview"
          description="All sites with current status and recent sign-in activity."
        />

        <Card>
          <CardHeader className="pb-2">
            <CardTitle>All sites</CardTitle>
            <CardDescription>
              Review every site record and open a site detail snapshot from the
              table.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sitesQuery.isError ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                Unable to load the sites list right now.
              </div>
            ) : sitesQuery.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : sites.length === 0 ? (
              <div className="rounded-md border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">
                No sites have been created yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Site</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Timezone</TableHead>
                    <TableHead className="text-right">Sign-ins</TableHead>
                    <TableHead className="text-right">Flagged</TableHead>
                    <TableHead>Last activity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sites.map((site) => (
                    <TableRow key={site.siteId}>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedSite({
                              id: site.siteId,
                              name: site.siteName,
                            })
                          }
                          className="text-left"
                        >
                          <span className="font-medium text-foreground">
                            {site.siteName}
                          </span>
                          <span className="mt-1 block text-xs text-muted-foreground">
                            {site.createdAt
                              ? `Created ${formatDistanceToNow(
                                  new Date(site.createdAt),
                                  {
                                    addSuffix: true,
                                  }
                                )}`
                              : "Created date unavailable"}
                          </span>
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant={siteStatusVariant(site.status)}>
                          {startCase(site.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[22rem] whitespace-normal text-muted-foreground">
                        {site.address}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {site.timezone}
                      </TableCell>
                      <TableCell className="text-right text-foreground">
                        {site.signInCount}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant={
                            site.flaggedSignIns > 0 ? "warning" : "outline"
                          }
                        >
                          {site.flaggedSignIns}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatRelative(site.lastSignInAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </PageContainer>

      <SiteDetailsDialog
        selection={selectedSite}
        onClose={() => setSelectedSite(null)}
      />
    </AdminGuard>
  );
}

function siteStatusVariant(status: string) {
  if (status === "active") {
    return "approved";
  }

  if (status === "planned") {
    return "secondary";
  }

  if (status === "completed") {
    return "ghost";
  }

  return "outline";
}

function formatRelative(value: string | null) {
  if (!value) {
    return "No sign-ins recorded";
  }

  return formatDistanceToNow(new Date(value), { addSuffix: true });
}

function startCase(value: string) {
  return value.replace(/\b\w/g, (match) => match.toUpperCase());
}
